/**
 * state.ts — Akili Brain: Central State & Persistence
 * All shared mutable state lives here. Modules import named refs; mutation
 * happens through the exported helpers so there is one clear write path.
 *
 * ── Self-Evolve additions ────────────────────────────────────────────────────
 *   toolCallStatus  – string label while a tool is running; null when idle
 *   empathyLine     – pending empathy text to prepend to next bot bubble
 *   workflows       – cached list from /api/ai/workflows
 *   personalFacts   – cached list from /api/ai/personal-facts
 *
 * ── Personalization Roadmap additions ───────────────────────────────────────
 *   [PHASE 2 & 5] persona             – current user persona
 *   [PHASE 3]     notificationSettings – notification prefs
 *   [PHASE 1]     threadRecall        – pending thread recall data
 *   [PHASE 4]     widgetState         – mini widget last-known state
 *   [PHASE 4]     widgetPollTimer     – setInterval handle for mini widget
 *
 * ── Kili Unified additions ───────────────────────────────────────────────────
 *   celebratedMilestones – tracks which milestone IDs have been toasted
 *   sentimentFromMeta    – last sentiment value received from SSE meta event
 *   assistantNickname    – user-assigned nickname for Kili; null = default
 *   currentAvatarEmotion – Plutchik emotion driving the current avatar state
 *   lastAvatarEmotion    – previous emotion; used for transition diffing
 */

import { newsState } from './state-news';

// ── Constants ─────────────────────────────────────────────────────────────────
export const ENDPOINT       = '/api/ai/chat';
export const DRAFT_ENDPOINT = '/api/ai/draft';
export const PREP_ENDPOINT  = '/api/ai/prep';
export const CONV_ENDPOINT  = '/api/ai/conversations';
export const STORAGE_KEY    = 'akili_ai_conv';
export const MAX_MSGS       = 80;

// ── Core state ────────────────────────────────────────────────────────────────
export const state = {
  open:               false,
  view:               'chat',
  conversationId:     null,
  streaming:          false,
  msgCount:           0,
  convCache:          null,
  unreadCount:        0,
  attachedDocumentId: null,
  attachedFileName:   '',
  currentMatterId:    null,
  draftMode:          false,
  draftDocType:       'letter',
  prepMode:           false,
  prepType:           'argument',

  // Voice
  mediaRecorder:      null,
  audioChunks:        [],
  isRecording:        false,

  // Audio visualiser
  audioCtx:           null,
  audioAnalyser:      null,
  audioVizRafId:      null,

  // FAB particles
  fabCanvas:          null,
  fabCtx:             null,
  fabParticles:       null,
  fabRafId:           null,
  mouseFabX:          36,
  mouseFabY:          36,

  // Persisted
  reactions:          {},
  recentDocs:         [],

  // Proactive suggestions (Agent 1)
  psDismissed:        {},
  psCollapsed:        false,
  psCount:            0,

  // ── Self-Evolve ──────────────────────────────────────────────────────────────
  toolCallStatus:     null,
  empathyLine:        null,

  // ── [PHASE 4.3] Client-side empathy detection ─────────────────────────────
  /**
   * When true, the frontend runs its own regex-based empathy-line splitter
   * (splitEmpathyLine) as a fallback after stream completion.
   * Should be false when the backend consistently emits the `empathy` field
   * on the SSE `meta` event — avoids double-rendering the empathy callout.
   * Toggled via window.AkiliBrain.setEmpathyDetect(on).
   */
  empathyDetect:      false,
  workflows:          [],
  personalFacts:      [],
  memorySnapshot:     null,

  // ── News / Signal Feed ────────────────────────────────────────────────────
  ...newsState,

  // ── [PHASE 2 & 5] Persona ─────────────────────────────────────────────────
  /**
   * The active persona for the current user.
   * One of: 'strategic_advisor' | 'efficiency_expert' | 'empathetic_partner'
   * Loaded on drawer open; updated on persona card selection.
   */
  persona:             null,

  // ── [PHASE 3] Notification Settings ──────────────────────────────────────
  /**
   * Cached notification preferences.
   * Shape: { briefing_time: string, deadline_reminders: bool, weekly_digest: bool }
   */
  notificationSettings: null,

  // ── [PHASE 1] Thread Recall ───────────────────────────────────────────────
  /**
   * Pending thread recall data from SSE `thread_recall` event.
   * Consumed by handlers.ts to show the recall banner.
   * Shape: { threadId, topic, summary } | null
   */
  threadRecall:         null,

  // ── [PHASE 4] Mini Widget ─────────────────────────────────────────────────
  /**
   * Last-known widget state from GET /api/ai/widget-state.
   * Shape: { unread_messages, upcoming_deadlines, suggested_action } | null
   */
  widgetState:          null,

  /** setInterval handle for the 60-second mini-widget poll. */
  widgetPollTimer:      null,

  // ── Kili Unified — Milestone & Sentiment ─────────────────────────────────
  /**
   * Tracks which milestone IDs have already been celebrated (toasted) in
   * the current session so we never fire the same confetti burst twice.
   * Keys are milestone ID strings; values are always true.
   */
  celebratedMilestones: {} as Record<string, boolean>,

  /**
   * The most recent sentiment value received from the SSE `meta` event.
   * One of: 'distress' | 'negative' | 'neutral' | 'positive'
   * Used by the brainwave canvas and emotion-avatar system to stay in sync
   * with the backend's real-time affect analysis.
   */
  sentimentFromMeta: 'neutral' as string,

  /**
   * Optional nickname the user has given to their Kili assistant.
   * Stored in localStorage as `akili_assistant_nickname`.
   * Null means the default name ("Kili") is displayed.
   */
  assistantNickname: null as string | null,

  /**
   * The Plutchik emotion currently driving the 3-D avatar and drawer ambient
   * theme. Written by emotion-avatar.ts after each meta/sentiment update.
   * E.g. 'neutral' | 'joy' | 'trust' | 'fear' | 'surprise' | 'sadness' |
   *       'anger' | 'anticipation' | 'love' | 'optimism' | 'awe' | 'remorse'
   */
  currentAvatarEmotion: 'neutral' as string,

  /**
   * The previous value of currentAvatarEmotion, captured just before each
   * emotion transition. Used by the avatar transition system to trigger the
   * correct interpolation animation between states.
   */
  lastAvatarEmotion: 'neutral' as string,
};

// ── Persistence helpers ────────────────────────────────────────────────────────
try { state.reactions  = JSON.parse(localStorage.getItem('akili_msg_reactions') || '{}'); } catch (_) {}
try { state.recentDocs = JSON.parse(localStorage.getItem('akili_recent_docs')    || '[]'); } catch (_) {}

// [PHASE 2 & 5] Persist persona across page loads
try {
  const storedPersona = localStorage.getItem('akili_persona');
  if (storedPersona) state.persona = storedPersona;
} catch (_) {}

// Persist assistant nickname across page loads
try {
  const storedNickname = localStorage.getItem('akili_assistant_nickname');
  if (storedNickname) state.assistantNickname = storedNickname;
} catch (_) {}

export function saveReactions() {
  try { localStorage.setItem('akili_msg_reactions', JSON.stringify(state.reactions)); } catch (_) {}
}

export function saveRecentDocs() {
  try { localStorage.setItem('akili_recent_docs', JSON.stringify(state.recentDocs)); } catch (_) {}
}

// [PHASE 2 & 5]
export function savePersona(persona: string) {
  try { localStorage.setItem('akili_persona', persona); } catch (_) {}
}

export function saveAssistantNickname(nickname: string | null) {
  try {
    if (nickname) {
      localStorage.setItem('akili_assistant_nickname', nickname);
    } else {
      localStorage.removeItem('akili_assistant_nickname');
    }
  } catch (_) {}
}

/** Clears conversation state for a fresh chat session. */
export function resetForNewConversation() {
  state.conversationId = null;
  state.msgCount       = 0;
  state.empathyLine    = null;
  state.toolCallStatus = null;
  state.threadRecall   = null;  // [PHASE 1]
  sessionStorage.removeItem(STORAGE_KEY);
}
